import {
	FunctionComponent,
	ReactElement,
	Suspense,
	useEffect,
	useRef
} from 'react'
import { useTranslation } from 'react-i18next'

import { useDisclosure } from '@chakra-ui/react'
import { DialogProps } from '@radix-ui/react-dialog'

import { Dialog, DialogContent } from '@/shared/ui/Dialog.tsx'

import { ConfirmationModal, ModalContext } from '../../index.ts'

interface ExtendedProps {
	isOpen: boolean
	onClose: () => void
	children?: ReactElement
	modalProps?: Omit<DialogProps, 'open' | 'onOpenChange'>
}

interface BasePropsSchema {
	onDirty: () => void
	onApply: (...params: never[]) => void
}

export const withUnsavedChangesModal =
	<BaseProps extends BasePropsSchema>(
		Component: FunctionComponent<BaseProps>,
		FallbackComponent: FunctionComponent<unknown>
	): FunctionComponent<Omit<BaseProps, 'onDirty'> & ExtendedProps> =>
	// eslint-disable-next-line react/display-name
	({
		isOpen,
		onClose,
		children,
		modalProps,
		...props
	}: Omit<BaseProps, 'onDirty'> & ExtendedProps) => {
		const { t } = useTranslation('unsaved-changes')

		const dirty = useRef(false)

		const {
			isOpen: isConfirmationModalOpen,
			onOpen: onConfirmationModalOpen,
			onClose: onConfirmationModalClose
		} = useDisclosure()

		useEffect(() => {
			if (isOpen === false) {
				dirty.current = false
			}
		}, [isOpen])

		const onMainModalClose = () => {
			if (dirty.current) {
				onConfirmationModalOpen()
				return
			}

			onClose()
		}

		const onMainModalApply = (...params: Parameters<BaseProps['onApply']>) => {
			props.onApply?.(...params)
			dirty.current = false
			onClose()
		}

		const onDirty = () => {
			dirty.current = true
		}

		const onLeaveModalClose = () => {
			onConfirmationModalClose()
			onClose()
		}

		return (
			<>
				<ModalContext.Provider value={{ isOpen, onClose }}>
					<Dialog
						open={isOpen}
						onOpenChange={open => {
							if (!open) onMainModalClose()
						}}
						{...modalProps}
					>
						<DialogContent>
							<Suspense fallback={<FallbackComponent />}>
								<Component
									onClose={onMainModalClose}
									// TODO: Remove as unknown
									{...(props as unknown as BaseProps)}
									onDirty={onDirty}
									onApply={onMainModalApply}
								/>
							</Suspense>
							{children}
						</DialogContent>
					</Dialog>
				</ModalContext.Provider>

				<Dialog
					open={isConfirmationModalOpen}
					onOpenChange={open => {
						if (!open) onConfirmationModalClose()
					}}
				>
					<DialogContent // TODO: change z-index to 100 after remove chakra ui
						slots={{ overlay: 'z-6500' }}
					>
						<ConfirmationModal
							title={t('title')}
							subTitle={t('subTitle')}
							onCancel={onConfirmationModalClose}
							onSubmit={onLeaveModalClose}
							cancelText={t('goBack')}
							submitText={t('continue')}
							submitProps={{
								backgroundColor: 'brand.300'
							}}
						/>
					</DialogContent>
				</Dialog>
			</>
		)
	}
